import React, { ReactElement, useMemo, useCallback, useState } from 'react';
import { Disclosure } from '@headlessui/react';
import {
  ChevronDownIcon,
  InformationCircleIcon,
} from '@heroicons/react/20/solid';

import {
  Alert,
  Checkbox,
  inputLabelClasses,
  MultiSelectCheckBox,
} from 'src/ui/components';
import EmailNotificationSettings from './EmailNotificationSettings';
import AccountQuestionSettings from './AccountQuestionSettings';
import { MultiSelectInputType } from 'src/features/CallDocuments/types/callDocumentList';

import {
  useGetPermissionGroupCode,
  useAuth,
  useGetPortalUserGroupCode,
  useGetStoredPermissions,
} from 'src/hooks';

import { useUserStore } from '../../store/userStore';

import {
  adminTooltipMsg,
  personLimiterInfoMsg,
} from '../../utils/userConstants';
import { limiterValidationMsg } from '../../utils/userConstants';
import { setupAlertsMsg } from '../../utils/userConstants';

import { PermissionGroupCodeType } from 'src/types/permissionGroupCode';
import { classNames } from 'src/utils/className';
import { Tooltip } from 'src/ui/components/Tooltip';

function UserSettings(): ReactElement {
  const { isExternalUser } = useAuth();

  // State for selected options per account (keyed by AccountId)
  // const [optionsByAccount, setOptionsByAccount] = useState<
  //   Record<number, Array<MultiSelectInputType>>
  // >({});

  const loggedInUserPermissions = useGetStoredPermissions();

  const { data: permissions, isLoading: isPermissionLoading } =
    useGetPermissionGroupCode();

  const userSettingsPermissions = useUserStore(
    useCallback(state => state?.userSettingsPermissions, []),
  );

  const updatePermissions = useUserStore(
    useCallback(state => state?.updatePermissions, []),
  );

  const updateIncludeEmptyNotification = useUserStore(
    useCallback(state => state.updateIncludeEmptyNotification, []),
  );

  const enableTab = useUserStore(
    React.useCallback(state => state.enableSettings, []),
  );

  const isAllEmailNotificationLoading = useUserStore(
    React.useCallback(state => state.isAllEmailNotificationLoading, []),
  );

  const isSaveClicked = useUserStore(
    React.useCallback(state => state.isSaveClicked, []),
  );

  const isViewPersonAlertsChecked = useUserStore(
    React.useCallback(state => state.isViewPersonAlertsChecked, []),
  );

  const setViewPersonAlertsChecked = useUserStore(
    React.useCallback(state => state.setViewPersonAlertsChecked, []),
  );

  const initialUserData = useUserStore(
    React.useCallback(state => state.initialUserData, []),
  );

  const updateDefaultNotification = useUserStore(
    React.useCallback(state => state.updateDefaultNotification, []),
  );

  const updateCustomEmailNotificationSettings = useUserStore(
    useCallback(state => state.updateCustomEmailNotificationSettings, []),
  );

  const setSelectedOptions = useUserStore(
    useCallback(state => state?.setSelectedOptions, []),
  );

  const updateAccountQuestionSettings = useUserStore(
    useCallback(state => state?.updateAccountQuestionSettings, []),
  );

  const accountQuestionSettings = useUserStore(
    useCallback(state => state?.accountQuestionSettings, []),
  );
  const loggedUserDTPermission = useUserStore(
    useCallback(state => state?.loggedUserDTPermission, []),
  );

  const accountList = useUserStore(
    useCallback(state => state?.accountList, []),
  );
  const currentData = useUserStore(
    useCallback(state => state?.currentData, []),
  );

  const accountsInfo = useUserStore(
    useCallback(state => state?.accountsInfo, []),
  );

  const optionsByAccount = useUserStore(
    useCallback(state => state.optionsByAccount ?? {}, []),
  );
  const setOptionsByAccount = useUserStore(
    useCallback(state => state.setOptionsByAccount, []),
  );
  const clearOptionsByAccount = useUserStore(
    useCallback(state => state.clearOptionsByAccount, []),
  );

  const updateOptionsByAccount = useUserStore(
    useCallback(state => state.updateOptionsByAccount, []),
  );

  const initialOptionsByAccount = useUserStore(
    useCallback(state => state.initialOptionsByAccount, []),
  );

  console.log('Accounts Info in User Settings:', accountQuestionSettings);
  console.log('optionsByAccount', optionsByAccount);

  const setSelectedRecords = useUserStore(
    useCallback(state => state?.setSelectedRecords, []),
  );

  const userMode = useUserStore(useCallback(state => state?.userMode, []));

  console.log('initialOptionsByAccount', initialOptionsByAccount);
  console.log('optionsByAccount', optionsByAccount);

  const getDisplayOptions = useCallback(
    (
      accountId: number,
      allAccountOptions: Array<MultiSelectInputType>,
    ): Array<MultiSelectInputType> => {
      const selectedOptions = optionsByAccount?.[accountId] ?? [];
      const nonSelectAllOptions = allAccountOptions.filter(o => o?.value !== 0);

      if (
        selectedOptions.length === nonSelectAllOptions.length &&
        nonSelectAllOptions.length > 0 &&
        selectedOptions.every(selected =>
          nonSelectAllOptions.some(opt => opt.value === selected.value),
        )
      ) {
        return allAccountOptions;
      }

      return selectedOptions;
    },
    [optionsByAccount],
  );

  const setOptionsForAccount = useCallback(
    (accountId: number, allOptions: Array<MultiSelectInputType> = []) =>
      (values: Array<MultiSelectInputType>) => {
        // If Select All (value === 0) chosen -> mark all options selected
        const hasSelectAll = values.some(v => v?.value === 0);
        const nonSelectAllOptions = allOptions.filter(o => o?.value !== 0);

        let selected: Array<MultiSelectInputType> = [];

        if (hasSelectAll) {
          selected = [...allOptions];
        } else {
          // remove any stray Select All value if present
          const filtered = values.filter(v => v?.value !== 0);
          // if user manually selected every item, treat as Select All
          if (
            filtered.length === nonSelectAllOptions.length &&
            nonSelectAllOptions.length > 0
          ) {
            selected = [...allOptions];
          } else {
            selected = filtered;
          }
        }

        setOptionsByAccount(
          (prev: Record<number, Array<MultiSelectInputType>>) => ({
            ...(prev || {}),
            [accountId]: selected,
          }),
        );
      },
    [setOptionsByAccount],
  );

  /*to check if documents is checked or not to show the section under Documents */
  const isDocumentsIncluded = useMemo(() => {
    let documentPresent = userSettingsPermissions?.findIndex(
      item => item?.ClaimType === 'DocumentTransmissionPermission',
    );
    if (documentPresent === -1 || documentPresent === undefined) return false;
    else return true;
  }, [userSettingsPermissions]);

  /*handle which permission is checked */
  function handleUpdatePermissions(permissionType: string) {
    let permissionsArray =
      userSettingsPermissions !== undefined ? [...userSettingsPermissions] : [];
    const index = userSettingsPermissions?.findIndex(
      item => item?.ClaimType === permissionType,
    );

    if (index !== -1 && index !== undefined) {
      permissionsArray?.splice(index, 1);
      if (permissionType === 'CanViewPersonAlerts') {
        const index = permissionsArray?.findIndex(
          item => item?.ClaimType === 'CanSetupPersonAlerts',
        );
        if (index !== -1 && index !== undefined) {
          permissionsArray?.splice(index, 1);
        }
      }
    } else {
      let newPermission = permissions?.Data?.find(
        item => item?.ClaimType === permissionType,
      );

      if (newPermission !== undefined && userSettingsPermissions !== undefined)
        permissionsArray = [
          ...userSettingsPermissions,
          {
            PermissionId: newPermission?.Id,
            PermissionName: newPermission?.Description,
            ClaimType: newPermission?.ClaimType,
          },
        ];
      else if (newPermission !== undefined) {
        let value = {
          PermissionId: newPermission?.Id,
          PermissionName: newPermission?.Description,
          ClaimType: newPermission?.ClaimType,
        };
        permissionsArray?.push(value);
      }
    }

    if (permissionsArray) updatePermissions([...permissionsArray]);
  }

  /*to show the check and uncheck mark in the permissions */
  function isPermissionChecked(description: string) {
    if (userSettingsPermissions === undefined) return false;
    else {
      let alreadyChecked = userSettingsPermissions?.findIndex(
        item => item?.ClaimType === description,
      );
      if (alreadyChecked !== -1) return true;
      else return false;
    }
  }

  function getAccountDetails(Id: number) {
    return accountList.find(x => x.AccountId === Id);
  }
  function resetOptionByAccount() {
    if (userMode == '') clearOptionsByAccount();
    else updateOptionsByAccount(initialOptionsByAccount);
  }

  //resetting the account setting values when calldocuments changed
  function resetValues() {
    updateIncludeEmptyNotification &&
      updateIncludeEmptyNotification(initialUserData?.includeEmptyNotification);
    updateDefaultNotification &&
      updateDefaultNotification(initialUserData?.defaultEmailNotification);
    updateCustomEmailNotificationSettings &&
      updateCustomEmailNotificationSettings(
        initialUserData?.customEmailNotificationSettings,
      );
    setSelectedOptions && setSelectedOptions(initialUserData?.selectedOptions);
    setSelectedRecords && setSelectedRecords(initialUserData?.selectedRecordId);

    const updatedAccountSettings = accountQuestionSettings?.map(obj2 => {
      return {
        ...obj2,
        AccessTypeId:
          isExternalUser && userMode == ''
            ? loggedUserDTPermission.find(
                acc => acc.AccountId === obj2?.AccountId,
              ).AccessTypeId
            : getAccountDetails(obj2.AccountId)?.AccessTypeID,
      };
    });

    updateAccountQuestionSettings(updatedAccountSettings);
  }

  const isEnablePermission = useCallback(
    (claimType: string) => {
      if (
        isExternalUser &&
        (claimType === 'CanAdministerUsers' ||
          (claimType === 'CanSendCoverageRequests' &&
            !loggedInUserPermissions.CanSendCoverageRequests) ||
          (claimType === 'CanSetupPersonAlerts' &&
            !loggedInUserPermissions.CanSetUpPersonAlerts) ||
          (claimType === 'CanManagePortalAlerts' &&
            !loggedInUserPermissions.CanManagePortalAlerts) ||
          (claimType === 'CanViewPersonAlerts' &&
            !loggedInUserPermissions.CanViewPersonAlerts) ||
          (claimType === 'CanSendCoverageRequests' &&
            !loggedInUserPermissions.CanSendCoverageRequests) ||
          (claimType === 'DocumentTransmissionPermission' &&
            !loggedInUserPermissions.CanViewDocumentTransmission))
      ) {
        return false;
      } else {
        return true;
      }
    },
    [isExternalUser, loggedInUserPermissions],
  );

  function renderPermissionCheckbox(): ReactElement {
    // to check if view person alerts is checked
    const isViewPersonAlertsChecked = isPermissionChecked(
      'CanViewPersonAlerts',
    );

    const isSetupPersonAlertsChecked = isPermissionChecked(
      'CanSetupPersonAlerts',
    );

    setViewPersonAlertsChecked(isViewPersonAlertsChecked);

    //to show setup person alerts after view person alerts
    let sortedPermissions = permissions?.Data ? [...permissions.Data] : [];
    const viewIdx = sortedPermissions.findIndex(
      p => p.ClaimType === 'CanViewPersonAlerts',
    );
    const setupIdx = sortedPermissions.findIndex(
      p => p.ClaimType === 'CanSetupPersonAlerts',
    );
    if (viewIdx !== -1 && setupIdx !== -1 && setupIdx < viewIdx) {
      const [setupPerm] = sortedPermissions.splice(setupIdx, 1);
      sortedPermissions.splice(viewIdx, 0, setupPerm);
    }

    return (
      <div className="space-y-4 divide-y rounded-md pt-4 dark:divide-neutral-700 dark:bg-neutral-800">
        <label className="block px-3 text-lg font-medium leading-6 text-gray-900 dark:text-white">
          Permissions
        </label>
        <div className="flex flex-col divide-y dark:divide-neutral-700">
          {sortedPermissions &&
            sortedPermissions.length > 0 &&
            sortedPermissions?.map((permission: PermissionGroupCodeType) => {
              const isSetupPersonAlerts =
                permission.ClaimType === 'CanSetupPersonAlerts' ||
                permission.Id === 3;

              const isViewPersonAlerts =
                permission.ClaimType === 'CanViewPersonAlerts' ||
                permission.Id === 4;

              //to check if any of the selected accounts has limiting values
              const hasAnyLimitingValues = accountQuestionSettings?.some(
                account =>
                  Array.isArray(account?.AccountCategoryLimitingValues) &&
                  account.AccountCategoryLimitingValues.length > 0,
              );

              // to show setup person alets only if view person alerts is checked
              if (isSetupPersonAlerts) {
                if (!isViewPersonAlertsChecked) {
                  return null;
                }
              }

              return (
                <div className="p-4" key={permission?.Id}>
                  {permission?.Id?.toString() != '3' && (
                    <Checkbox
                      id={permission?.Id?.toString()}
                      label={permission?.Description}
                      key={permission?.Id}
                      checked={isPermissionChecked(permission?.ClaimType)}
                      onChange={e => {
                        handleUpdatePermissions(permission?.ClaimType);
                        if (permission?.Id?.toString() === '7') {
                          resetValues();
                        }
                        if (permission?.Id?.toString() === '4') {
                          resetOptionByAccount();
                        }
                      }}
                      title={
                        !isEnablePermission(permission.ClaimType) &&
                        isExternalUser
                          ? adminTooltipMsg
                          : ''
                      }
                      disabled={!isEnablePermission(permission.ClaimType)}
                    />
                  )}

                  {isSetupPersonAlerts && (
                    <>
                      <div className="flex items-center gap-4">
                        <Checkbox
                          id={permission?.Id?.toString()}
                          label={permission?.Description}
                          key={permission?.Id}
                          checked={isPermissionChecked(permission?.ClaimType)}
                          onChange={e => {
                            handleUpdatePermissions(permission?.ClaimType);
                          }}
                          title={
                            !isEnablePermission(permission.ClaimType) &&
                            isExternalUser
                              ? adminTooltipMsg
                              : ''
                          }
                          disabled={!isEnablePermission(permission.ClaimType)}
                        />
                        <Alert
                          variant="info"
                          className={classNames(
                            'max-w-fit',
                            !(
                              isSetupPersonAlertsChecked && hasAnyLimitingValues
                            ) && 'invisible',
                          )}
                          size="small"
                        >
                          {setupAlertsMsg}
                        </Alert>
                      </div>
                    </>
                  )}

                  {isViewPersonAlerts &&
                    isViewPersonAlertsChecked &&
                    hasAnyLimitingValues && (
                      <div className="mt-3 flex flex-col gap-1 pl-7">
                        <div className="mt-2 flex items-center gap-1">
                          <label className="text-sm font-medium leading-6 text-gray-900 after:ml-0.5 after:text-red-400 after:content-['*'] dark:text-white ">
                            Set person alert limiter{' '}
                          </label>

                          <Tooltip
                            trigger={
                              <InformationCircleIcon
                                className="-mb-0.5 -ml-0.5 mr-1 h-5 w-5 text-primary"
                                aria-hidden="true"
                              />
                            }
                            content={personLimiterInfoMsg}
                          />
                        </div>
                        <ul
                          className={` ${
                            (accountQuestionSettings?.length ?? 0) >= 1
                              ? 'list-none divide-y divide-light-light rounded-md border border-gray-200 bg-white dark:divide-dark-medium dark:border-dark-medium dark:bg-neutral-800'
                              : ''
                          }`}
                        >
                          {accountQuestionSettings?.map(accountset => {
                            if (
                              !accountset?.AccountCategoryLimitingValues?.length
                            )
                              return null;

                            const noOptionsSelected =
                              !optionsByAccount ||
                              !optionsByAccount[accountset.AccountId] ||
                              optionsByAccount[accountset.AccountId].length ===
                                0;

                            return (
                              <li
                                key={accountset?.AccountId}
                                className="flex flex-col p-4 text-sm leading-6 xl:grid xl:grid-cols-3 xl:items-center xl:px-3 xl:py-2"
                              >
                                <div className="flex min-w-0 max-w-full gap-2 xl:col-span-1 xl:items-center">
                                  {noOptionsSelected && isSaveClicked && (
                                    <div className="h-3 w-3">
                                      <span className="relative flex h-3 w-3 items-center justify-center">
                                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                                        <span className="relative inline-flex h-2 w-2 gap-3 rounded-full bg-red-500" />
                                      </span>
                                    </div>
                                  )}
                                  <div className="prevent-text-breakout mb-3 xl:mb-0">
                                    <span className="text-base font-medium text-gray-900 dark:text-white lg:text-sm">
                                      {`${accountset?.CID}-${accountset?.SID}-${
                                        accountset?.PID
                                      }${
                                        accountset?.AccountName
                                          ? `_${accountset?.AccountName}`
                                          : ''
                                      }`}
                                    </span>
                                  </div>
                                </div>
                                <div className="items-center gap-4 xl:col-span-2 xl:grid xl:grid-cols-4">
                                  <label
                                    htmlFor={`personAlertLimiter-${accountset?.AccountId}`}
                                    className={`${inputLabelClasses} prevent-text-breakout col-span-3 text-xs dark:text-white xl:text-right xl:text-sm`}
                                  >
                                    {`Person Alert Limiter - ${accountset?.LimitCategory}`}
                                  </label>
                                  {(() => {
                                    const accountOptions: MultiSelectInputType[] =
                                      [
                                        // { label: 'Select All', value: 0 },
                                        { label: 'Select All', value: 0 },
                                        ...(accountset?.AccountCategoryLimitingValues?.map(
                                          item => ({
                                            label: item.Name,
                                            value: item.Id,
                                          }),
                                        ).sort((a, b) => a.value - b.value) ??
                                          []),
                                      ];
                                    const displayOptions = getDisplayOptions(
                                      accountset?.AccountId ?? 0,
                                      accountOptions,
                                    );

                                    return (
                                      <MultiSelectCheckBox
                                        id={`user-multiselect-${accountset?.AccountId}`}
                                        label="User Selection"
                                        options={accountOptions}
                                        selectedOptions={displayOptions}
                                        setSelectedOptions={setOptionsForAccount(
                                          accountset?.AccountId ?? 0,
                                          accountOptions,
                                        )}
                                        isSearchable={true}
                                        position="right"
                                      />
                                    );
                                  })()}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                </div>
              );
            })}
        </div>
      </div>
    );
  }

  return (
    <>
      {!(isAllEmailNotificationLoading || isPermissionLoading) && (
        <Disclosure as="div" defaultOpen={userMode === 'Edit'} className="pb-4">
          {({ open }) => {
            return (
              <>
                <Disclosure.Button className="flex w-full items-center justify-between rounded-lg bg-white px-4 py-2 text-left shadow-sm hover:bg-gray-50 focus:outline-none focus-visible:ring focus-visible:ring-light-focus disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 ui-open:rounded-b-none dark:bg-neutral-800 dark:hover:bg-neutral-800/70">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      {!enableTab('permission-tab') && isSaveClicked && (
                        <div className="h-3 w-3">
                          <span className="relative flex h-3 w-3 items-center justify-center">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                          </span>
                        </div>
                      )}
                      <h3 className="font-medium">User settings</h3>
                    </div>
                  </div>
                  <ChevronDownIcon
                    className={`${
                      open ? 'rotate-180 transform' : ''
                    } h-5 w-5 text-gray-500 dark:text-white`}
                  />
                </Disclosure.Button>

                <Disclosure.Panel
                  className="rounded-b-lg border-t bg-gray-50 text-sm text-gray-500 shadow dark:border-neutral-700/50 dark:bg-neutral-800"
                  id="user-settings"
                >
                  {renderPermissionCheckbox()}

                  <div className="flex flex-col gap-3 pb-4 pl-11 pr-4">
                    {isDocumentsIncluded && <EmailNotificationSettings />}
                    {isDocumentsIncluded && (
                      <AccountQuestionSettings open={open} />
                    )}
                  </div>
                </Disclosure.Panel>
              </>
            );
          }}
        </Disclosure>
      )}
    </>
  );
}

export { UserSettings };
